import { useEffect } from 'react'

/**
 * ClientCarousel - continuously scrolling client-logo marquee.
 *
 * The markup ships one non-wrapping strip of `.cc-logo` items inside
 * `[data-client-carousel] .cc-track`. This overlay:
 *   - clones the strip enough times to more than fill the viewport, so the
 *     loop never runs out of content;
 *   - drives the scroll with requestAnimationFrame at a constant px/second
 *     speed (independent of screen size), translating the track and wrapping
 *     the offset by exactly one original-strip width - because the clone is
 *     identical, the wrap is invisible (seamless, no jump);
 *   - pauses on hover / keyboard focus;
 *   - lets the user drag / swipe to scrub (pointer events, so mouse + touch),
 *     then resumes auto-scroll from wherever they let go - the loop never
 *     breaks;
 *   - recomputes on resize (ResizeObserver) so it stays correct and smooth on
 *     every screen size;
 *   - respects prefers-reduced-motion (no auto-scroll; drag still works).
 *
 * Follows the DOM-overlay pattern (RevealFixer, DetailsAutoClose, …): a
 * MutationObserver picks up the homepage when HtmlPage injects it.
 */
const SPEED = 42 // px per second - constant regardless of viewport width

function initMarquee(root) {
  if (root.dataset.ccReady === '1') return
  const viewport = root.querySelector('.cc-viewport')
  const track = root.querySelector('.cc-track')
  const originals = track ? Array.from(track.children) : []
  if (!viewport || !track || originals.length === 0) return
  root.dataset.ccReady = '1'

  let baseWidth = 0

  // Clone the original strip until the track is at least 2× the viewport, so
  // there is always content to scroll into view. Re-run on resize.
  function fill() {
    track.querySelectorAll('[data-cc-clone]').forEach((n) => n.remove())
    baseWidth = originals.reduce((w, el) => w + el.getBoundingClientRect().width, 0)
    if (baseWidth <= 0) return
    const need = viewport.getBoundingClientRect().width * 2 + baseWidth
    let total = baseWidth
    while (total < need) {
      originals.forEach((el) => {
        const c = el.cloneNode(true)
        c.setAttribute('data-cc-clone', '')
        c.setAttribute('aria-hidden', 'true')
        // Clones are decorative duplicates of content already in the DOM, so
        // they must stay out of the tab order - otherwise a marquee carrying
        // links (the industries strip) would hand keyboard users several
        // identical, aria-hidden tab stops per item.
        if (c.matches('a, button, [tabindex]')) c.setAttribute('tabindex', '-1')
        c.querySelectorAll('a, button, [tabindex]').forEach((n) => n.setAttribute('tabindex', '-1'))
        track.appendChild(c)
      })
      total += baseWidth
    }
  }

  let pos = 0
  let paused = false
  let dragging = false
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  function wrap() {
    if (baseWidth <= 0) return
    // Keep pos within (-baseWidth, 0]; content is periodic with that period.
    pos = ((pos % baseWidth) + baseWidth) % baseWidth - baseWidth
    if (pos <= -baseWidth) pos += baseWidth
  }
  function apply() {
    track.style.transform = `translate3d(${pos}px,0,0)`
  }

  fill()
  apply()

  // ---- rAF auto-scroll ----
  let last = null
  function frame(t) {
    const dt = last == null ? 0 : (t - last) / 1000
    last = t
    // Skip advancing while hovered, dragging, off-screen, or reduced-motion.
    if (!paused && !dragging && !reduce && root.offsetParent !== null && dt > 0) {
      pos -= SPEED * Math.min(dt, 0.05) // clamp dt so a tab-switch can't jump
      wrap()
      apply()
    }
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)

  // ---- hover / focus pause ----
  viewport.addEventListener('mouseenter', () => { paused = true })
  viewport.addEventListener('mouseleave', () => { paused = false })
  root.addEventListener('focusin', () => { paused = true })
  root.addEventListener('focusout', () => { paused = false })

  // ---- drag / swipe (pointer events cover mouse + touch) ----
  let startX = 0
  let startPos = 0
  let activeId = null
  viewport.addEventListener('pointerdown', (e) => {
    dragging = true
    activeId = e.pointerId
    startX = e.clientX
    startPos = pos
    viewport.classList.add('is-dragging')
    try { viewport.setPointerCapture(e.pointerId) } catch { /* older browsers */ }
  })
  viewport.addEventListener('pointermove', (e) => {
    if (!dragging || e.pointerId !== activeId) return
    pos = startPos + (e.clientX - startX)
    wrap()
    apply()
  })
  function endDrag() {
    if (!dragging) return
    dragging = false
    activeId = null
    viewport.classList.remove('is-dragging')
    last = null // avoid a dt spike when auto-scroll resumes
  }
  viewport.addEventListener('pointerup', endDrag)
  viewport.addEventListener('pointercancel', endDrag)
  // Stop the browser's native image/link ghost-drag from hijacking the swipe.
  viewport.addEventListener('dragstart', (e) => e.preventDefault())

  // ---- responsive recompute ----
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => { fill(); wrap(); apply() })
    ro.observe(viewport)
  } else {
    window.addEventListener('resize', () => { fill(); wrap(); apply() })
  }
}

export default function ClientCarousel() {
  useEffect(() => {
    function initAll() {
      // [data-client-carousel] is the original client-logo strip; [data-marquee]
      // is the same mechanic reused for any other content (the homepage
      // industries strip). Nothing here is logo-specific - it clones and scrolls
      // whatever .cc-track holds.
      document
        .querySelectorAll(
          '[data-client-carousel]:not([data-cc-ready="1"]), [data-marquee]:not([data-cc-ready="1"])',
        )
        .forEach(initMarquee)
    }
    initAll()
    const mo = new MutationObserver(initAll)
    mo.observe(document.body, { childList: true, subtree: true })
    return () => mo.disconnect()
  }, [])
  return null
}
